import { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ViewList as ListIcon,
  ViewModule as GridIcon,
  Warning as WarningIcon,
  Medication as MedicationIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup, Grid, Tooltip, Stack } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import HoofIcon from '../../components/HoofIcon';
import HorsePhotoAvatar from '../../components/HorsePhotoAvatar';
import HorseFormDialog from '../../components/HorseFormDialog';
import PatientCard from './PatientCard';
import InsightsPanel from './InsightsPanel';
import UnallocatedResultsCard from '../../components/Inbox/UnallocatedResultsCard';
import { useInfiniteQuery } from 'react-query';
import {
  horseApi,
  PaginatedResponse,
  Horse,
} from '../../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatBreed } from '../../utils/breed';
import { withOfflineFallback, readHorse, readHorsesPage } from '../../offline';
import { useSnackbar } from 'notistack';
import { useRBAC } from '../../auth/useRBAC';
import { patientsControls } from '../../auth/pageControls';

/**
 * Patient directory. Wave 3 of the chart redesign collapsed the old split
 * list/detail layout into a list-only surface — clicking a row navigates to
 * `/patients/:id` where the unified chart (HorseProfile) does the per-patient
 * work. Add/Edit Patient still lives here because creating a new horse is a
 * list-level action, not a chart-level one.
 */
const PatientList: FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // horses.controller.ts `create` → manage:patients (INV-108). This page IS the one the product
  // owner described: an empty list with an Add button, for somebody the server refuses on submit.
  const rbac = useRBAC();
  const can = patientsControls(rbac);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE' | 'DECEASED'>('ALL');
  // Add/Edit Patient dialog — the form itself lives in the shared
  // HorseFormDialog component (also used by both chart views).
  const [horseDialog, setHorseDialog] = useState<{ open: boolean; horse: Horse | null }>({
    open: false,
    horse: null,
  });
  // List vs grid view preference — sticky across reloads so the vet keeps
  // the layout they prefer without re-selecting it every session.
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    try {
      return localStorage.getItem('stabletrack.patientView') === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  const changeViewMode = (next: 'list' | 'grid') => {
    setViewMode(next);
    try {
      localStorage.setItem('stabletrack.patientView', next);
    } catch {
      /* noop */
    }
  };

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Server-side paginated horses list — infinite scroll, scoped to current
  // status + search filter so we never pull the whole clinic at once.
  const PAGE_SIZE = 50;
  const horsesQuery = useInfiniteQuery<PaginatedResponse<Horse>>(
    ['horses', { search: searchTerm, status: statusFilter }],
    ({ pageParam = 1 }) =>
      withOfflineFallback(
        () =>
          horseApi.listPaginated({
            page: pageParam,
            limit: PAGE_SIZE,
            search: searchTerm || undefined,
            status: statusFilter === 'ALL' ? undefined : statusFilter,
          }),
        () =>
          readHorsesPage({
            page: pageParam,
            limit: PAGE_SIZE,
            search: searchTerm || undefined,
            status: statusFilter === 'ALL' ? undefined : statusFilter,
          }) as any,
      )(),
    {
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const horses = useMemo<Horse[]>(
    () => (horsesQuery.data?.pages.flatMap((p) => p.data) as Horse[] | undefined) ?? [],
    [horsesQuery.data],
  );
  const totalHorses = horsesQuery.data?.pages[0]?.total ?? 0;

  // Infinite-scroll sentinel.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (
        entries[0]?.isIntersecting &&
        horsesQuery.hasNextPage &&
        !horsesQuery.isFetchingNextPage
      ) {
        horsesQuery.fetchNextPage();
      }
    },
    [horsesQuery],
  );
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(observerCallback, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [observerCallback]);

  const statusCount = useMemo(
    () => ({
      ALL: statusFilter === 'ALL' ? totalHorses : undefined,
      ACTIVE: statusFilter === 'ACTIVE' ? totalHorses : undefined,
      INACTIVE: statusFilter === 'INACTIVE' ? totalHorses : undefined,
      DECEASED: statusFilter === 'DECEASED' ? totalHorses : undefined,
    }),
    [statusFilter, totalHorses],
  );

  function handleOpenHorseDialog(horse?: Horse) {
    setHorseDialog({ open: true, horse: horse ?? null });
  }

  // Deep link from the Clients page ("Edit owners on the patient file"):
  // open the Edit Patient dialog for the requested horse directly — the
  // owners/percentages form lives in this dialog, not on the chart.
  const location = useLocation();
  useEffect(() => {
    const state = location.state as { editHorseId?: string } | null;
    const editHorseId = state?.editHorseId;
    if (!editHorseId) return;
    // One-shot: clear the state so back/refresh doesn't reopen the dialog
    window.history.replaceState({}, document.title);
    (async () => {
      try {
        const horse = await withOfflineFallback(
          () => horseApi.get(editHorseId),
          () => readHorse(editHorseId) as any,
        )();
        if (horse) {
          handleOpenHorseDialog(horse as Horse);
        } else {
          // Offline fallback resolves undefined (rather than throwing) for a
          // missing row — same feedback as the catch, never a silent no-op.
          enqueueSnackbar('Could not open that patient — find it in the list below.', { variant: 'error' });
        }
      } catch {
        enqueueSnackbar('Could not open that patient — find it in the list below.', { variant: 'error' });
      }
    })();
    // eslint-disable-next-line
  }, [location.state]);

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1, sm: 2 } }}>
      <Box sx={{ py: { xs: 1, sm: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' }, fontWeight: 600 }}
            >
              Patients
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Browse and search patients — click any row to open the chart.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: { xs: 'stretch', sm: 'center' } }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && changeViewMode(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid rgba(139,94,52,0.25)',
                  px: 1.25,
                  py: 0.5,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(201,119,43,0.12)',
                    color: '#C9772B',
                    '&:hover': { bgcolor: 'rgba(201,119,43,0.18)' },
                  },
                },
              }}
            >
              <ToggleButton value="list" aria-label="List view" data-testid="view-toggle-list">
                <ListIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="Card view" data-testid="view-toggle-grid">
                <GridIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
            {can.add && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenHorseDialog()}
              data-tour="horse-add-button"
              data-testid="add-patient-button"
              sx={{
                minHeight: 40,
                borderRadius: 2,
                textTransform: 'none',
                bgcolor: '#C9772B',
                '&:hover': { bgcolor: '#6B4727' },
              }}
            >
              Add Patient
            </Button>
            )}
          </Box>
        </Box>

        {can.unmatchedResults && <UnallocatedResultsCard />}

        <Grid container spacing={1.5}>
          {/* Main column: search, status filter, then list or card grid */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, patient ID, owner or microchip…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  data-testid="patient-search"
                />
                <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <MenuItem value="ALL">All Patients{statusCount.ALL !== undefined ? ` (${statusCount.ALL})` : ''}</MenuItem>
                    <MenuItem value="ACTIVE">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        Active{statusCount.ACTIVE !== undefined ? ` (${statusCount.ACTIVE})` : ''}
                      </Box>
                    </MenuItem>
                    <MenuItem value="INACTIVE">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.500' }} />
                        Inactive{statusCount.INACTIVE !== undefined ? ` (${statusCount.INACTIVE})` : ''}
                      </Box>
                    </MenuItem>
                    <MenuItem value="DECEASED">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                        Deceased{statusCount.DECEASED !== undefined ? ` (${statusCount.DECEASED})` : ''}
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {horsesQuery.isLoading && horses.length === 0 ? (
              <Paper sx={{ py: 6, textAlign: 'center', borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                <CircularProgress size={28} />
              </Paper>
            ) : horses.length === 0 ? (
              <Paper sx={{ py: 6, textAlign: 'center', borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                <HoofIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  {searchTerm ? `No patients match "${searchTerm}"` : 'No patients yet'}
                </Typography>
                {/* "No patients yet" stays — it is true either way. The call to action does not:
                    the reported bug was this exact button opening a form that refuses on save. */}
                {!searchTerm && can.addFirst && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenHorseDialog()}
                    sx={{ mt: 1 }}
                  >
                    Add the first patient
                  </Button>
                )}
              </Paper>
            ) : viewMode === 'grid' ? (
              // Card grid view — denser visual scan, surfaces flags per tile.
              <>
                <Grid container spacing={1.5} data-testid="patient-grid">
                  {horses.map((horse) => (
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={horse.id}>
                      <PatientCard
                        horse={horse}
                        onOpen={() => navigate(`/patients/${horse.id}`)}
                        // The tile's pencil opens the same dialog the toolbar Add does and saves
                        // through PUT /horses/:id, so it needs the same slug. `undefined` is what
                        // hides it — the card renders no pencil without a handler.
                        onEdit={can.edit ? () => handleOpenHorseDialog(horse) : undefined}
                      />
                    </Grid>
                  ))}
                </Grid>
                {horsesQuery.hasNextPage && (
                  <Box ref={loadMoreRef} sx={{ textAlign: 'center', py: 2 }}>
                    {horsesQuery.isFetchingNextPage && <CircularProgress size={20} />}
                  </Box>
                )}
              </>
            ) : (
              <Paper
                sx={{
                  borderRadius: 3,
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
                data-testid="patient-list"
              >
                <List sx={{ p: 0 }} data-tour="horse-list">
                  {horses.map((horse, idx) => {
                    const criticalAlerts = Array.isArray(horse.medicalAlerts)
                      ? (horse.medicalAlerts as any[]).filter((a) => typeof a === 'object' && a?.isCritical)
                      : [];
                    const activeMeds = horse.activeMedicationCount ?? 0;
                    const lastVisit = horse.lastVisitAt ? new Date(horse.lastVisitAt) : null;
                    const owner = horse.owners?.[0];
                    return (
                      <Box key={horse.id}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItem
                          button
                          onClick={() => navigate(`/patients/${horse.id}`)}
                          sx={{
                            py: 1.25,
                            px: { xs: 1.5, sm: 2 },
                            // Hover-only badges on the right of the row
                            '& .row-hover-actions': { opacity: 0, transition: 'opacity 120ms' },
                            '&:hover .row-hover-actions': { opacity: 1 },
                          }}
                          data-testid={`patient-row-${horse.id}`}
                          secondaryAction={
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pr: 0.5 }}>
                              {lastVisit && (
                                <Tooltip title={`Last visit: ${lastVisit.toLocaleDateString()}`} arrow>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontSize: '0.72rem', mr: 1, whiteSpace: 'nowrap' }}
                                  >
                                    {formatDistanceToNow(lastVisit, { addSuffix: true })}
                                  </Typography>
                                </Tooltip>
                              )}
                              <Box className="row-hover-actions" sx={{ display: 'flex', gap: 0.25 }}>
                                {owner?.email && (
                                  <Tooltip title={`Email ${owner.name}`} arrow>
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={`mailto:${owner.email}`}
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ p: 0.5 }}
                                    >
                                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenHorseDialog(horse);
                                }}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto' }}
                              >
                                Edit
                              </Button>
                            </Stack>
                          }
                        >
                          <ListItemAvatar>
                            <HorsePhotoAvatar horseId={horse.id} photoUrl={horse.photoUrl} size={40} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {horse.name}
                                </Typography>
                                {horse.patientId && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}
                                  >
                                    {horse.patientId}
                                  </Typography>
                                )}
                                {(horse.status === 'ACTIVE' || !horse.status) && (
                                  <Chip size="small" label="Active" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                                {horse.status === 'INACTIVE' && (
                                  <Chip size="small" label="Inactive" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                                {horse.status === 'DECEASED' && (
                                  <Chip size="small" label="Deceased" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#7F1D1D', color: '#fff', borderRadius: '999px', '& .MuiChip-label': { px: 1 } }} />
                                )}
                                {criticalAlerts.length > 0 && (
                                  <Tooltip
                                    title={criticalAlerts.map((a: any) => a.text).join(' · ')}
                                    arrow
                                  >
                                    <Chip
                                      icon={<WarningIcon sx={{ fontSize: 12 }} />}
                                      label={criticalAlerts.length === 1 ? criticalAlerts[0].text : `${criticalAlerts.length} alerts`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.68rem',
                                        maxWidth: 220,
                                        bgcolor: '#E65100',
                                        color: '#fff',
                                        '& .MuiChip-icon': { ml: 0.5, color: '#fff' },
                                        '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {activeMeds > 0 && (
                                  <Tooltip title={`${activeMeds} active medication${activeMeds === 1 ? '' : 's'}`} arrow>
                                    <Chip
                                      icon={<MedicationIcon sx={{ fontSize: 12 }} />}
                                      label={`Rx ${activeMeds}`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.68rem',
                                        bgcolor: 'rgba(46,125,50,0.12)',
                                        color: '#2E7D32',
                                        border: '1px solid rgba(46,125,50,0.3)',
                                        '& .MuiChip-icon': { ml: 0.5, color: '#2E7D32' },
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {formatBreed(horse)}
                                {' · '}
                                Owner: {owner ? owner.name : '—'}
                                {horse.owners && horse.owners.length > 1 && ` +${horse.owners.length - 1} more`}
                                {horse.location && ` · ${horse.location}`}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </Box>
                    );
                  })}
                  {horsesQuery.hasNextPage && (
                    <Box ref={loadMoreRef} sx={{ textAlign: 'center', py: 2 }}>
                      {horsesQuery.isFetchingNextPage && <CircularProgress size={20} />}
                    </Box>
                  )}
                </List>
              </Paper>
            )}
          </Grid>

          {/* Right rail — Today / Need attention / Recently viewed */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 12 } }}>
              <InsightsPanel horses={horses} />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Add/Edit Patient dialog — shared component, also mounted by the
          Classic and Modern chart views for the in-place Edit button. */}
      <HorseFormDialog
        open={horseDialog.open}
        horse={horseDialog.horse}
        onClose={() => setHorseDialog((prev) => ({ ...prev, open: false }))}
      />
    </Container>
  );
};

export default PatientList;
